import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { capitalize, upperCase } from 'lodash';
import { Calendar } from 'lucide-react';

import {
    COMPLETE_STATUS_COLOR,
    IN_PROGRESS_STATUS_COLOR,
    NOT_STARTED_STATUS_COLOR,
    ON_HOLD_STATUS_COLOR,
} from '@/lib/constants';

import { Card, CardContent } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import {
    type GanttFeature,
    GanttFeatureList,
    GanttFeatureRow,
    GanttHeader,
    GanttProvider,
    GanttSidebar,
    GanttSidebarGroup,
    GanttSidebarItem,
    type GanttStatus,
    GanttTimeline,
    GanttToday,
} from '@/features/end-of-lease/gantt';

import {
    CreateTaskDialog,
    CreateTaskFormValues,
    SelectOption,
} from './ProjectDetailView/CreateTaskDialog';
import {
    EditTaskDialog,
    EditTaskFormValues,
} from './ProjectDetailView/EditTaskDialog';
import { TimelineCard } from './TimelineCard';
import { ProjectTask, ProjectTaskParty, ProjectTaskStatus } from './types';

interface Props {
    tasks: ProjectTask[];
    isCreateTaskDialogOpen: boolean;
    isEditTaskDialogOpen: boolean;
    toggleEditDialogOpen: () => void;
    parties: SelectOption[];
    persons: SelectOption[];
    priorities: SelectOption[];
    statusOptions: SelectOption[];
    selectedTask: ProjectTask | null;
    onTaskClick: (task: ProjectTask) => void;
    createTaskForm: UseFormReturn<CreateTaskFormValues>;
    onCreateDialogOpenChange: (open: boolean) => void;
    onCreateTaskSubmit: (data: CreateTaskFormValues) => void;
    editTaskForm: UseFormReturn<EditTaskFormValues>;
    onEditTaskSubmit: (data: EditTaskFormValues) => void;
}

export default function ProjectTimeline({
    tasks = [],
    isCreateTaskDialogOpen,
    isEditTaskDialogOpen,
    toggleEditDialogOpen,
    parties,
    persons,
    priorities,
    statusOptions,
    selectedTask,
    onTaskClick,
    createTaskForm,
    onCreateDialogOpenChange,
    onCreateTaskSubmit,
    editTaskForm,
    onEditTaskSubmit,
}: Props) {
    // Map base status options to Gantt status format for visualization
    const ganttStatuses: Record<ProjectTaskStatus, GanttStatus> = useMemo(
        () => ({
            [ProjectTaskStatus.Completed]: {
                id: 'completed',
                name: 'Completed',
                color: COMPLETE_STATUS_COLOR,
            },
            [ProjectTaskStatus.InProgress]: {
                id: 'in-progress',
                name: 'In Progress',
                color: IN_PROGRESS_STATUS_COLOR,
            },
            [ProjectTaskStatus.OnHold]: {
                id: 'on-hold',
                name: 'On Hold',
                color: ON_HOLD_STATUS_COLOR,
            },
            [ProjectTaskStatus.NotStarted]: {
                id: 'not-started',
                name: 'Not Started',
                color: NOT_STARTED_STATUS_COLOR,
            },
        }),
        [],
    );

    // Transform activities to GanttFeature format
    const ganttFeatures: GanttFeature[] = useMemo(
        () =>
            tasks.map((task) => ({
                id: task.id.toString(),
                name: task.name,
                startAt: new Date(task.startDate),
                endAt: new Date(task.endDate),
                status: ganttStatuses[task.status],
                lane: task.party, // Use party as lane for row grouping
            })),
        [tasks, ganttStatuses],
    );

    const lessorFeatures = useMemo(
        () => ganttFeatures.filter((f) => f.lane === ProjectTaskParty.Lessor),
        [ganttFeatures],
    );
    const camoFeatures = useMemo(
        () => ganttFeatures.filter((f) => f.lane === ProjectTaskParty.CAMO),
        [ganttFeatures],
    );
    const mroFeatures = useMemo(
        () => ganttFeatures.filter((f) => f.lane === ProjectTaskParty.MRO),
        [ganttFeatures],
    );

    return (
        <div data-testid="ProjectTimeline">
            <div className="flex items-center mb-4">
                <div className="flex items-center text-xl font-bold text-slate-900">
                    <Calendar className="h-5 w-5 text-blue-600 mr-2" />
                    Project Timeline
                </div>
                <Form {...createTaskForm}>
                    <CreateTaskDialog
                        parties={parties}
                        persons={persons}
                        priorities={priorities}
                        isOpen={isCreateTaskDialogOpen}
                        onOpenChange={onCreateDialogOpenChange}
                        onSubmit={onCreateTaskSubmit}
                    />
                </Form>
            </div>
            {/* Calculate width based on project view layout dimensions from Tailwind config */}
            <Card className="w-[calc(100vw-theme(spacing.project-view-sidebar-width)-theme(spacing.project-view-content-padding))] bg-white shadow-xs mb-4">
                <CardContent className="p-0">
                    {ganttFeatures.length === 0 ? (
                        <div
                            className="flex flex-col items-center justify-center py-12 text-muted-foreground"
                            data-testid="ProjectTimeline__emptyState"
                        >
                            <Calendar className="h-12 w-12 mb-4 opacity-50" />
                            <p className="text-lg font-medium">No tasks yet</p>
                            <p className="text-sm">
                                Create your first task to get started
                            </p>
                        </div>
                    ) : (
                        <div>
                            <GanttProvider
                                range="monthly"
                                zoom={150}
                                rowHeight={76}
                                maxHeight={500}
                            >
                                <GanttSidebar>
                                    {lessorFeatures.length > 0 && (
                                        <GanttSidebarGroup
                                            name={capitalize(
                                                ProjectTaskParty.Lessor,
                                            )}
                                            data-testid="GanttSidebarGroup-Lessor"
                                        >
                                            {lessorFeatures.map((feature) => (
                                                <GanttSidebarItem
                                                    key={feature.id}
                                                    feature={feature}
                                                />
                                            ))}
                                        </GanttSidebarGroup>
                                    )}
                                    {camoFeatures.length > 0 && (
                                        <GanttSidebarGroup
                                            name={upperCase(
                                                ProjectTaskParty.CAMO,
                                            )}
                                            data-testid="GanttSidebarGroup-CAMO"
                                        >
                                            {camoFeatures.map((feature) => (
                                                <GanttSidebarItem
                                                    key={feature.id}
                                                    feature={feature}
                                                />
                                            ))}
                                        </GanttSidebarGroup>
                                    )}
                                    {mroFeatures.length > 0 && (
                                        <GanttSidebarGroup
                                            name={upperCase(
                                                ProjectTaskParty.MRO,
                                            )}
                                            data-testid="GanttSidebarGroup-MRO"
                                        >
                                            {mroFeatures.map((feature) => (
                                                <GanttSidebarItem
                                                    key={feature.id}
                                                    feature={feature}
                                                />
                                            ))}
                                        </GanttSidebarGroup>
                                    )}
                                </GanttSidebar>
                                <GanttTimeline>
                                    <GanttHeader />
                                    <GanttToday />
                                    <GanttFeatureList>
                                        {lessorFeatures.length > 0 && (
                                            <>
                                                <div
                                                    style={{ height: '4px' }}
                                                />
                                                {lessorFeatures.map(
                                                    (feature) => {
                                                        return (
                                                            <GanttFeatureRow
                                                                key={feature.id}
                                                                features={[
                                                                    feature,
                                                                ]}
                                                            >
                                                                {(f) => (
                                                                    <TimelineCard
                                                                        feature={
                                                                            f
                                                                        }
                                                                        tasks={
                                                                            tasks
                                                                        }
                                                                        party={
                                                                            ProjectTaskParty.Lessor
                                                                        }
                                                                        onTaskClick={
                                                                            onTaskClick
                                                                        }
                                                                    />
                                                                )}
                                                            </GanttFeatureRow>
                                                        );
                                                    },
                                                )}
                                            </>
                                        )}
                                        {camoFeatures.length > 0 && (
                                            <>
                                                <div
                                                    style={{ height: '4px' }}
                                                />
                                                {camoFeatures.map((feature) => {
                                                    return (
                                                        <GanttFeatureRow
                                                            key={feature.id}
                                                            features={[feature]}
                                                        >
                                                            {(f) => (
                                                                <TimelineCard
                                                                    feature={f}
                                                                    tasks={
                                                                        tasks
                                                                    }
                                                                    party={
                                                                        ProjectTaskParty.CAMO
                                                                    }
                                                                    onTaskClick={
                                                                        onTaskClick
                                                                    }
                                                                />
                                                            )}
                                                        </GanttFeatureRow>
                                                    );
                                                })}
                                            </>
                                        )}
                                        {mroFeatures.length > 0 && (
                                            <>
                                                <div
                                                    style={{ height: '4px' }}
                                                />
                                                {mroFeatures.map((feature) => {
                                                    return (
                                                        <GanttFeatureRow
                                                            key={feature.id}
                                                            features={[feature]}
                                                        >
                                                            {(f) => (
                                                                <TimelineCard
                                                                    feature={f}
                                                                    tasks={
                                                                        tasks
                                                                    }
                                                                    party={
                                                                        ProjectTaskParty.MRO
                                                                    }
                                                                    onTaskClick={
                                                                        onTaskClick
                                                                    }
                                                                />
                                                            )}
                                                        </GanttFeatureRow>
                                                    );
                                                })}
                                            </>
                                        )}
                                    </GanttFeatureList>
                                </GanttTimeline>
                            </GanttProvider>
                            <div className="p-0 border-t border-border">
                                <div className="flex items-center gap-6 px-6 py-4">
                                    <div className="text-sm text-muted-foreground">
                                        Legend:
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded bg-blue-600 border border-blue-800" />
                                        <span className="text-sm text-muted-foreground">
                                            {capitalize(
                                                ProjectTaskParty.Lessor,
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded bg-emerald-600 border border-emerald-800" />
                                        <span className="text-sm text-muted-foreground">
                                            {upperCase(ProjectTaskParty.CAMO)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded bg-amber-600 border border-amber-800" />
                                        <span className="text-sm text-muted-foreground">
                                            {upperCase(ProjectTaskParty.MRO)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
            {selectedTask && (
                <Form {...editTaskForm}>
                    <EditTaskDialog
                        statuses={statusOptions}
                        persons={persons}
                        priorities={priorities}
                        isOpen={isEditTaskDialogOpen}
                        toggleOpen={toggleEditDialogOpen}
                        currentTask={selectedTask}
                        onSubmit={onEditTaskSubmit}
                    />
                </Form>
            )}
        </div>
    );
}
